import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ExternalLink, Link2, RefreshCw, Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { DataTable } from '../../../components/shared/DataTable';
import { EmptyState } from '../../../components/shared/EmptyState';
import { LoadingState } from '../../../components/shared/LoadingState';
import { ErrorState } from '../../../components/shared/ErrorState';
import { useJira, JiraProject } from '../../../core/hooks/useJira';
import { useClient } from '../../../core/hooks/useClient';
import { useTickets } from '../../../core/hooks/useTickets';
import { TicketDialog } from './TicketDialog';
import { TicketDeleteDialog } from './TicketDeleteDialog';
import type { Ticket, TicketStatus, TicketPriority } from '../../../core/models/Ticket';
import { TicketStatus as TicketStatusEnum, TicketPriority as TicketPriorityEnum, TicketType as TicketTypeEnum } from '../../../core/models/Ticket';
import { ColumnDef } from '@tanstack/react-table';

interface ClientJiraTabProps {
  clientId: string;
  currentProjectId?: string;
}

export const ClientJiraTab: React.FC<ClientJiraTabProps> = ({ clientId, currentProjectId }) => {
  const { config, getProjects, linkClientToProject } = useJira();
  const { client } = useClient(clientId);
  const projectId = currentProjectId || client?.jiraProjectId;
  const { tickets, loading: ticketsLoading, error: ticketsError, createTicket, updateTicket, deleteTicket } = useTickets(projectId);

  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(currentProjectId || '');
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [deletingTicket, setDeletingTicket] = useState<Ticket | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Get the project key from the projects list
  const projectKey = useMemo(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      return project?.key;
    }
    return undefined;
  }, [projectId, projects]);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const projectsList = await getProjects();
      setProjects(projectsList);
    } catch (error) {
      console.error('Failed to load Jira projects:', error);
    } finally {
      setLoading(false);
    }
  }, [getProjects]);

  useEffect(() => {
    if (config?.accessToken) {
      loadProjects();
    }
  }, [config?.accessToken, loadProjects]);

  const handleLinkProject = async () => {
    if (selectedProjectId) {
      try {
        await linkClientToProject(clientId, selectedProjectId);
        alert('Project linked successfully!');
      } catch (error) {
        console.error('Failed to link project:', error);
        alert('Failed to link project. Please try again.');
      }
    }
  };

  const handleCreateTicket = () => {
    setEditingTicket(null);
    setTicketDialogOpen(true);
  };

  const handleEditTicket = (ticket: Ticket) => {
    setEditingTicket(ticket);
    setTicketDialogOpen(true);
  };

  const handleDeleteTicket = (ticket: Ticket) => {
    setDeletingTicket(ticket);
    setDeleteDialogOpen(true);
  };

  const handleTicketSubmit = async (data: Omit<Ticket, 'id' | 'key' | 'lastSyncedAt' | 'created' | 'projectId'>) => {
    if (!projectId) {
      throw new Error('No project ID available');
    }

    if (editingTicket) {
      await updateTicket(projectId, editingTicket.id, data);
    } else {
      await createTicket(projectId, data, projectKey);
    }
    setTicketDialogOpen(false);
    setEditingTicket(null);
  };

  const handleTicketDelete = async () => {
    if (!deletingTicket || !projectId) {
      return;
    }
    await deleteTicket(projectId, deletingTicket.id);
    setDeleteDialogOpen(false);
    setDeletingTicket(null);
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatusEnum.DONE:
      case TicketStatusEnum.RESOLVED:
        return 'success';
      case TicketStatusEnum.IN_PROGRESS:
        return 'warning';
      case TicketStatusEnum.CLOSED:
        return 'default';
      case TicketStatusEnum.TO_DO:
      default:
        return 'outline';
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case TicketPriorityEnum.CRITICAL:
        return 'error';
      case TicketPriorityEnum.HIGH:
        return 'warning';
      case TicketPriorityEnum.MEDIUM:
        return 'default';
      case TicketPriorityEnum.LOW:
        return 'outline';
      default:
        return 'outline';
    }
  };

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      if (statusFilter !== 'all' && ticket.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && ticket.priority !== priorityFilter) return false;
      if (typeFilter !== 'all' && ticket.type !== typeFilter) return false;
      return true;
    });
  }, [tickets, statusFilter, priorityFilter, typeFilter]);

  const columns: ColumnDef<Ticket>[] = [
    {
      accessorKey: 'key',
      header: 'Key',
      cell: ({ row }) => (
        <div className="font-mono font-medium">{row.original.key}</div>
      ),
    },
    {
      accessorKey: 'summary',
      header: 'Summary',
      cell: ({ row }) => (
        <div className="font-medium max-w-md truncate" title={row.original.summary}>
          {row.original.summary}
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">
          {row.original.type}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getStatusColor(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge variant={getPriorityColor(row.original.priority)}>
          {row.original.priority}
        </Badge>
      ),
    },
    {
      accessorKey: 'created',
      header: 'Created',
      cell: ({ row }) => {
        const date = row.original.created || row.original.lastSyncedAt;
        return date ? new Date(date).toLocaleDateString() : '-';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditTicket(row.original)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteTicket(row.original)}
          >
            <Trash2 className="h-4 w-4 text-error" />
          </Button>
          {row.original.url && (
            <Button
              variant="ghost"
              size="sm"
              asChild
            >
              <a
                href={row.original.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!config?.accessToken) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              title="Jira not connected"
              description="Connect your Jira account in Settings to link Jira projects to this project. You can still manage tickets directly in Firestore."
            />
          </CardContent>
        </Card>
        {projectId && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Tickets</h3>
              <Button onClick={handleCreateTicket} disabled={!projectId}>
                <Plus className="mr-2 h-4 w-4" />
                Create Ticket
              </Button>
            </div>
            {ticketsLoading ? (
              <LoadingState message="Loading tickets..." />
            ) : ticketsError ? (
              <ErrorState message="Failed to load tickets" />
            ) : filteredTickets.length === 0 ? (
              <EmptyState
                title="No tickets found"
                description={tickets.length === 0 ? "Create your first ticket to get started" : "No tickets match the current filters"}
                action={
                  tickets.length === 0
                    ? {
                        label: 'Create Ticket',
                        onClick: handleCreateTicket,
                      }
                    : undefined
                }
              />
            ) : (
              <>
                <div className="flex gap-2 mb-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {Object.values(TicketStatusEnum).map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      {Object.values(TicketPriorityEnum).map(priority => (
                        <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {Object.values(TicketTypeEnum).map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DataTable columns={columns} data={filteredTickets} searchKey="summary" searchPlaceholder="Search tickets..." />
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return <LoadingState message="Loading Jira projects..." />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Link Jira Project</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Connect a Jira project to this project to track issues and tickets
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a Jira project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.key} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleLinkProject} disabled={!selectedProjectId}>
                <Link2 className="mr-2 h-4 w-4" />
                Link Project
              </Button>
              <Button variant="outline" size="icon" onClick={loadProjects}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            {currentProjectId && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Currently Linked Project</p>
                    <p className="text-sm text-muted-foreground">
                      {projects.find(p => p.id === currentProjectId)?.name || currentProjectId}
                    </p>
                  </div>
                  {config.baseUrl && projects.find(p => p.id === currentProjectId)?.key && (
                    <Button variant="ghost" size="sm" asChild>
                      <a
                        href={`${config.baseUrl}/browse/${projects.find(p => p.id === currentProjectId)?.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        View in Jira
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Section */}
      {projectId && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Tickets</h3>
            <Button onClick={handleCreateTicket} disabled={!projectId}>
              <Plus className="mr-2 h-4 w-4" />
              Create Ticket
            </Button>
          </div>

          {ticketsLoading ? (
            <LoadingState message="Loading tickets..." />
          ) : ticketsError ? (
            <ErrorState message="Failed to load tickets" />
          ) : filteredTickets.length === 0 ? (
            <EmptyState
              title="No tickets found"
              description={tickets.length === 0 ? "Create your first ticket to get started" : "No tickets match the current filters"}
              action={
                tickets.length === 0
                  ? {
                      label: 'Create Ticket',
                      onClick: handleCreateTicket,
                    }
                  : undefined
              }
            />
          ) : (
            <>
              <div className="flex gap-2 mb-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.values(TicketStatusEnum).map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    {Object.values(TicketPriorityEnum).map(priority => (
                      <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.values(TicketTypeEnum).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DataTable columns={columns} data={filteredTickets} searchKey="summary" searchPlaceholder="Search tickets..." />
            </>
          )}
        </div>
      )}

      {/* Ticket Dialog */}
      {projectId && (
        <TicketDialog
          open={ticketDialogOpen}
          onClose={() => {
            setTicketDialogOpen(false);
            setEditingTicket(null);
          }}
          ticket={editingTicket || undefined}
          projectId={projectId}
          projectKey={projectKey}
          onSubmit={handleTicketSubmit}
        />
      )}

      {/* Delete Dialog */}
      {deletingTicket && (
        <TicketDeleteDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setDeletingTicket(null);
          }}
          ticket={deletingTicket}
          onConfirm={handleTicketDelete}
        />
      )}
    </div>
  );
};
